import { clamp } from "modules/clamp";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import { appStateMutate } from "stores/modules/app.state/operations";
import { getAppState } from "stores/modules/app.state/selectors";

type Result = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export const OVER_EDGE_THRESHOLD = 20;
const SPEED_SLOWER = 6;
const SPEED_FASTER = 12;

const calcTopOrLeft = (client: number, scale: number) => {
  if (client < 1) {
    return SPEED_FASTER * scale;
  } else if (client < OVER_EDGE_THRESHOLD) {
    return SPEED_SLOWER * scale;
  }
  return 0;
};

const calcRightOrBottom = (client: number, size: number, scale: number) => {
  if (client > size) {
    return SPEED_FASTER * scale;
  } else if (client > size - OVER_EDGE_THRESHOLD) {
    return SPEED_SLOWER * scale;
  }
  return 0;
};

export const useOverDrag = (
  viewEl: HTMLDivElement | null,
  centerEl: HTMLDivElement | null,
  onOver?: (over: Result, viewRect?: DOMRect) => void
) => {
  const overRef = useRef<Result>({ left: 0, right: 0, top: 0, bottom: 0 });
  const interval = useRef<number | null>(null);
  const dispatch = useAppDispatch();
  const currentX = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isMultiSelectMode = useAppSelector((state) =>
    getAppState(state, "isMultiSelectMode")
  );
  const selectedTransform = useAppSelector((state) =>
    getAppState(state, "selectedTransform")
  );
  const isNotSelectDragging =
    selectedTransform.x === 0 && selectedTransform.y === 0;
  const [isOverDragging, setIsOverDragging] = useState<boolean>(false);
  const scale = useAppSelector((state) =>
    getAppState(state, "roomScreenScale")
  );

  const clear = useCallback(() => {
    if (interval.current) window.clearInterval(interval.current);
    if (currentX.current !== 0 || currentY.current !== 0) {
      const nextPosition = {
        x: Math.round(currentX.current),
        y: Math.round(currentY.current),
      };
      dispatch(
        appStateMutate((state) => {
          state.roomScreenPosition = nextPosition;
        })
      );
      currentX.current = 0;
      currentY.current = 0;
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOverDragging && centerEl) {
      const style = getComputedStyle(centerEl);
      const transform = style.transform;
      const matrix = new DOMMatrixReadOnly(transform);
      currentX.current = matrix.m41;
      currentY.current = matrix.m42;
      const viewRect = viewEl?.getBoundingClientRect();
      interval.current = window.setInterval(() => {
        const over = overRef.current;
        const overX = over.left || -over.right;
        const overY = over.top || -over.bottom;
        currentX.current += overX;
        currentY.current += overY;
        centerEl.style.transform = `translate(${currentX.current}px, ${currentY.current}px)`;

        if (onOver) onOver(over, viewRect);
      }, 20);
    }

    return () => {
      clear();
    };
  }, [centerEl, isOverDragging, viewEl, clear, onOver]);

  useEffect(() => {
    if (!isMultiSelectMode && isNotSelectDragging) {
      clear();
    }
  }, [isMultiSelectMode, isNotSelectDragging, clear]);

  const onDragOver = useCallback(
    (clientX: number, clientY: number) => {
      if (!viewEl) return;

      const rect = viewEl.getBoundingClientRect();
      const calcScale = clamp(scale, { min: 0.5, max: 2 });
      const left = calcTopOrLeft(clientX, calcScale);
      const top = calcTopOrLeft(clientY, calcScale);
      const right = calcRightOrBottom(clientX, rect.width, calcScale);
      const bottom = calcRightOrBottom(clientY, rect.height, calcScale);

      if (left || top || right || bottom) {
        overRef.current = {
          left,
          top,
          right,
          bottom,
        };
        setIsOverDragging(true);
      } else {
        setIsOverDragging(false);
      }
    },
    [viewEl, scale]
  );

  const onDragOverStop = useCallback(() => {
    setIsOverDragging(false);
  }, []);

  return { onDragOver, onDragOverStop };
};
